import { useState, useMemo } from 'react'
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Button,
} from '@mui/material'
import { useBodyScrollLock } from 'src/hooks/useBodyScrollLock'
import Close from '@mui/icons-material/Close'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Person from '@mui/icons-material/Person'
import VideoCall from '@mui/icons-material/VideoCall'
import AccessTime from '@mui/icons-material/AccessTime'
import CalendarToday from '@mui/icons-material/CalendarToday'
import Info from '@mui/icons-material/Info'
import Business from '@mui/icons-material/Business'
import LocationOn from '@mui/icons-material/LocationOn'
import PlayArrow from '@mui/icons-material/PlayArrow'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { styled } from '@mui/material/styles'
import dayjs from 'dayjs'
import {
  SESSION_PLATFORM_OFFLINE,
  SESSION_PLATFORM_ONLINE,
  BOOKING_STATUSES,
  BOOKING_PAYMENT_STATUSES,
  OFFLINE_PAYMENT_METHODS_LIST,
  GENDER_LIST_ONBOARDING,
  displayNamesOfEventTypes,
  BOOKING_SESSION_STATUS_MAP,
} from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getOrdinalSuffixForSuperScript } from 'src/consumer/helpers/utilFunctions'
import { getBookedSlotStripColor } from '../DayViewCalendarComponent'
import BookedSlotActionsAccordion from './BookedSlotActionsAccordion'

// Styled Components
const DrawerContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 255, 0.98) 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
`

const DrawerHeader = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(67, 56, 202, 0.1);
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.04) 0%, rgba(99, 102, 241, 0.04) 100%);
  position: sticky;
  top: 0;
  z-index: 10;
  gap: 12px;
  
  @media (max-width: 768px) {
    padding: 16px 20px;
    gap: 10px;
  }
`

const DrawerHeaderTop = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const DrawerTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
  color: rgba(67, 56, 202, 0.95);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  letter-spacing: -0.02em;
  flex: 1;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`

const DrawerHeaderInfo = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`

const HeaderInfoItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  border: 1px solid rgba(67, 56, 202, 0.1);
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    gap: 4px;
  }
`

const HeaderInfoText = styled(Typography)`
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.75);
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const HeaderInfoLabel = styled(Typography)`
  font-size: 11px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`

const DrawerContent = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  
  @media (max-width: 768px) {
    padding: 12px 16px;
  }
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.02);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(67, 56, 202, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(67, 56, 202, 0.3);
    }
  }
`

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: '12px !important',
  marginBottom: '12px !important',
  border: '1px solid rgba(67, 56, 202, 0.1)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  overflow: 'hidden',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 12px 0 !important',
    boxShadow: '0 4px 16px rgba(67, 56, 202, 0.12)',
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '56px',
    padding: '0 20px',
    '&.Mui-expanded': {
      minHeight: '56px',
      backgroundColor: 'rgba(67, 56, 202, 0.04)',
    },
  },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0',
    alignItems: 'center',
  },
  '& .MuiAccordionDetails-root': {
    padding: '20px',
    backgroundColor: '#fff',
  },
}))

const AccordionTitle = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`

const AccordionIcon = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
  color: rgba(67, 56, 202, 0.9);
`

const InfoSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InfoRow = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: rgba(67, 56, 202, 0.02);
  border-radius: 8px;
  border-left: 3px solid rgba(67, 56, 202, 0.2);
`

const InfoLabel = styled(Typography)`
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 120px;
`

const InfoValue = styled('div')`
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  flex: 1;
`

const StatusChip = styled(Chip)`
  font-weight: 600;
  font-size: 11px;
  height: 24px;
`

const HeroActionsSection = styled(Box)`
  padding: 20px 24px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.04) 0%, rgba(99, 102, 241, 0.04) 100%);
  border-bottom: 1px solid rgba(67, 56, 202, 0.1);
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  @media (max-width: 768px) {
    padding: 16px 20px;
    gap: 10px;
  }
`

const HeroActionButton = styled(Button)`
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  font-size: 15px;
  box-shadow: 0 4px 16px rgba(67, 56, 202, 0.25);
  transition: all 0.2s ease;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(67, 56, 202, 0.35);
    background: linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%);
  }
  
  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 14px;
  }
`

const SecondaryActionButton = styled(Button)`
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 500;
  text-transform: none;
  font-size: 13px;
  color: rgba(67, 56, 202, 0.85);
  border-color: rgba(67, 56, 202, 0.3);
  
  &:hover {
    background: rgba(67, 56, 202, 0.08);
    border-color: rgba(67, 56, 202, 0.5);
  }
`

/**
 * Comprehensive Booked Event Details Drawer
 * Displays event details and all available actions in accordions
 * Role and portal aware
 */
export default function BookedEventDetailsDrawer({
  open,
  onClose,
  bookedSlot,
  // Action handlers
  onOpenJoiningDetails,
  onOpenEventLogs,
  onOpenReportView,
  onOpenPrescriptionDownload,
  onOpenUploadPrescription,
  onSendReportToDoctor,
  onBookScreening,
  onSendForTherapy,
  onSendForAssessment,
  onOpenTreatmentHistory,
  onBookTherapy,
  onRequestReassign,
  onToggleRecording,
  onMarkCompleted,
  onOpenPatientDetails,
  onAddVitals,
  onEditBooking,
  onReschedule,
  onOpenPaymentLinks,
  onGoToEvent, // Hero action: Start/View Consult/Therapy
  // Props
  forMentor = false,
  showViewReportsButton = false,
  showPrescriptionPdfDownloadButtons = false,
  forceShowJoiningDetails = false,
  hideRescheduleDialog = false,
  refreshBookedEvents,
  sendSecureSessionLinkToPatientSignature,
  sendPublicSessionLinkToPatientSignature,
  showSendingLinkButtonsToSupport = false,
  clinicAssociations = [],
  // Additional props for BookedSlotActionsAccordion
  setClickedBookedSlotData,
  handleNewTherapyRequest,
  therapyBookingDataForThisEvent,
  openEmrImagesDialog,
  openReceiptDialog,
  setEventDataForReceiptPrinting,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  
  // Lock body scroll when drawer is open
  useBodyScrollLock(open, 1300)
  
  const isSupportPortal = typeof window !== 'undefined' && window.location?.pathname?.includes('/support')
  const isBookedSlotBlockedForPayment =
    bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
    bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING

  // High Contrast toggle state (persisted in localStorage)
  const [highContrast, setHighContrast] = useState(() => {
    try {
      return localStorage.getItem('calendarHeaderHighContrast') === 'true'
    } catch {
      return false
    }
  })
  
  // Handle high contrast toggle
  const handleHighContrastToggle = (event) => {
    const newValue = event.target.checked
    setHighContrast(newValue)
    try {
      localStorage.setItem('calendarHeaderHighContrast', newValue.toString())
      // Dispatch event to notify calendar header
      window.dispatchEvent(new CustomEvent('calendar-header-high-contrast-change', {
        detail: { enabled: newValue }
      }))
    } catch (error) {
      console.error('Error saving high contrast preference:', error)
    }
  }

  // Helper function to safely execute action handlers
  const safeActionHandler = (handler, actionName) => {
    return () => {
      try {
        if (handler && typeof handler === 'function') {
          handler()
        }
      } catch (error) {
        console.error(`Error executing ${actionName}:`, error)
      }
    }
  }

  // Get clinic data from associations if available
  const clinicData = useMemo(() => {
    if (bookedSlot?.clinicId && clinicAssociations?.length > 0) {
      const clinic = clinicAssociations.find((c) => c.clinicId === bookedSlot.clinicId)
      return clinic
        ? {
            clinicBrandName: clinic.clinicBrandName,
            clinicLocationName: clinic.title,
          }
        : null
    }
    return null
  }, [bookedSlot?.clinicId, clinicAssociations])

  const slotColor = useMemo(() => {
    try {
      if (!bookedSlot) return null
      return getBookedSlotStripColor(bookedSlot, false, true) || { background: 'rgba(0, 0, 0, 0.1)', fontColor: 'rgba(0, 0, 0, 0.7)' }
    } catch (error) {
      console.error('Error getting slot color:', error)
      return { background: 'rgba(0, 0, 0, 0.1)', fontColor: 'rgba(0, 0, 0, 0.7)' }
    }
  }, [bookedSlot])

  // Format time for display
  const eventTimeFormatted = useMemo(() => {
    try {
      if (!bookedSlot?.startTime) return null
      const start = dayjs.utc(bookedSlot.startTime)
      const end = dayjs.utc(bookedSlot.endTime || bookedSlot.startTime)
      
      if (!start.isValid() || !end.isValid()) return null
      
      return {
        date: start.local().format('DD MMM YYYY'),
        time: `${start.local().format('h:mm A')} - ${end.local().format('h:mm A')}`,
        full: start.local().format('DD MMM YYYY, h:mm A'),
      }
    } catch (error) {
      console.error('Error formatting event time:', error)
      return null
    }
  }, [bookedSlot?.startTime, bookedSlot?.endTime])

  // Get patient name
  const patientName = useMemo(() => {
    try {
      return bookedSlot?.userName || bookedSlot?.patientInfo?.name || 'Unknown Patient'
    } catch (error) {
      console.error('Error getting patient name:', error)
      return 'Unknown Patient'
    }
  }, [bookedSlot?.userName, bookedSlot?.patientInfo?.name])
  
  // Get event type display name
  const eventTypeName = useMemo(() => {
    try {
      return displayNamesOfEventTypes?.[bookedSlot?.eventType] || 'Session'
    } catch (error) {
      console.error('Error getting event type name:', error)
      return 'Session'
    }
  }, [bookedSlot?.eventType])
  
  // Get visit number if available
  const visitNumber = useMemo(() => {
    try {
      if (!bookedSlot?.patientMeetingSeqNumber) return null
      const suffix = getOrdinalSuffixForSuperScript(bookedSlot.patientMeetingSeqNumber) || ''
      return `${bookedSlot.patientMeetingSeqNumber}${suffix} Visit`
    } catch (error) {
      console.error('Error getting visit number:', error)
      return null
    }
  }, [bookedSlot?.patientMeetingSeqNumber])

  if (!bookedSlot) return null

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '600px',
          maxWidth: isMobile ? '100%' : '90vw',
          height: isMobile ? '90vh' : '100%',
          maxHeight: isMobile ? '90vh' : '100%',
          borderTopLeftRadius: isMobile ? '20px' : 0,
          borderTopRightRadius: isMobile ? '20px' : 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
      }}
      ModalProps={{
        disableScrollLock: true, // We handle scroll lock with useBodyScrollLock hook
      }}
    >
      <DrawerContainer>
        {/* Drag Handle for Mobile Bottom Sheet */}
        {isMobile && (
          <Box
            sx={{
              width: '40px',
              height: '4px',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '2px',
              margin: '8px auto',
              cursor: 'grab',
              '&:active': {
                cursor: 'grabbing',
              },
            }}
          />
        )}
        <DrawerHeader>
          <DrawerHeaderTop>
            <DrawerTitle>{patientName}</DrawerTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={highContrast}
                    onChange={handleHighContrastToggle}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'rgba(67, 56, 202, 0.8)',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'rgba(67, 56, 202, 0.5)',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'rgba(67, 56, 202, 0.7)',
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    High Contrast
                  </Typography>
                }
                sx={{ margin: 0 }}
              />
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'rgba(67, 56, 202, 0.7)',
                  padding: isMobile ? '12px' : '8px',
                  minWidth: isMobile ? '48px' : '40px',
                  minHeight: isMobile ? '48px' : '40px',
                  '&:hover': {
                    backgroundColor: 'rgba(67, 56, 202, 0.1)',
                  },
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </DrawerHeaderTop>
          
          <DrawerHeaderInfo>
            {eventTypeName && (
              <HeaderInfoItem>
                <CalendarToday sx={{ fontSize: 16, color: 'rgba(67, 56, 202, 0.7)' }} />
                <Box>
                  <HeaderInfoLabel>Event Type</HeaderInfoLabel>
                  <HeaderInfoText>{eventTypeName}</HeaderInfoText>
                </Box>
              </HeaderInfoItem>
            )}
            
            {eventTimeFormatted && eventTimeFormatted.date && (
              <HeaderInfoItem>
                <AccessTime sx={{ fontSize: 16, color: 'rgba(67, 56, 202, 0.7)' }} />
                <Box>
                  <HeaderInfoLabel>Date & Time</HeaderInfoLabel>
                  <HeaderInfoText>
                    {eventTimeFormatted.date} • {eventTimeFormatted.time}
                  </HeaderInfoText>
                </Box>
              </HeaderInfoItem>
            )}
            
            {visitNumber && (
              <HeaderInfoItem>
                <Person sx={{ fontSize: 16, color: 'rgba(67, 56, 202, 0.7)' }} />
                <Box>
                  <HeaderInfoLabel>Visit</HeaderInfoLabel>
                  <HeaderInfoText>{visitNumber}</HeaderInfoText>
                </Box>
              </HeaderInfoItem>
            )}
            
            {bookedSlot?.status && (
              <HeaderInfoItem>
                <StatusChip
                  label={bookedSlot.status}
                  size="small"
                  sx={{
                    backgroundColor: slotColor?.background || 'rgba(0, 0, 0, 0.1)',
                    color: slotColor?.fontColor || 'rgba(0, 0, 0, 0.7)',
                    height: '20px',
                    fontSize: '10px',
                    fontWeight: 600,
                  }}
                />
              </HeaderInfoItem>
            )}
            
            {bookedSlot?.meetingSrc && (
              <HeaderInfoItem>
                {bookedSlot.meetingSrc === SESSION_PLATFORM_ONLINE ? (
                  <VideoCall sx={{ fontSize: 16, color: 'rgba(16, 185, 129, 0.8)' }} />
                ) : (
                  <Person sx={{ fontSize: 16, color: 'rgba(245, 158, 11, 0.8)' }} />
                )}
                <Box>
                  <HeaderInfoLabel>Platform</HeaderInfoLabel>
                  <HeaderInfoText>
                    {bookedSlot.meetingSrc === SESSION_PLATFORM_ONLINE ? 'Online' : 'Offline'}
                  </HeaderInfoText>
                </Box>
              </HeaderInfoItem>
            )}
          </DrawerHeaderInfo>
        </DrawerHeader>

        <DrawerContent>
          {/* Hero Actions Section - Visible first in Mentor Portal and only for booked status */}
          {forMentor && 
           !isSupportPortal && 
           onGoToEvent && 
           bookedSlot?.status === BOOKING_STATUSES.BOOKED && (
            <HeroActionsSection>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'rgba(67, 56, 202, 0.6)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '4px',
                  }}
                >
                  Quick Actions
                </Typography>
                <HeroActionButton
                  variant="contained"
                  startIcon={<PlayArrow sx={{ fontSize: 20 }} />}
                  onClick={safeActionHandler(() => {
                    onGoToEvent(bookedSlot)
                    onClose()
                  }, 'go to event')}
                  disabled={isBookedSlotBlockedForPayment}
                  sx={{
                    width: '100%',
                  }}
                >
                  {bookedSlot?.markedCompleted ? 'View/Edit' : 'Start'}{' '}
                  {eventTypeName}
                </HeroActionButton>
                {!bookedSlot?.markedCompleted && onMarkCompleted && (
                  <SecondaryActionButton
                    variant="outlined"
                    startIcon={<CheckCircle sx={{ fontSize: 18 }} />}
                    onClick={safeActionHandler(() => {
                      onMarkCompleted()
                    }, 'mark as completed')}
                    sx={{
                      width: '100%',
                    }}
                  >
                    Mark as Completed
                  </SecondaryActionButton>
                )}
              </Box>
            </HeroActionsSection>
          )}

          {/* Event Overview Accordion */}
          <StyledAccordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />}>
              <AccordionTitle>
                <AccordionIcon>
                  <Info sx={{ fontSize: 20 }} />
                </AccordionIcon>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.85)' }}>
                    Event Overview
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '11px' }}>
                    Basic information and status
                  </Typography>
                </Box>
              </AccordionTitle>
            </AccordionSummary>
            <AccordionDetails>
              <InfoSection>
                <InfoRow>
                  <InfoLabel>Patient</InfoLabel>
                  <InfoValue>
                    {(() => {
                      try {
                        const name = bookedSlot?.userName || bookedSlot?.patientInfo?.name || 'N/A'
                        const patientInfo = bookedSlot?.patientInfo
                        if (!patientInfo) return name
                        
                        const genderLabel = GENDER_LIST_ONBOARDING?.find(
                          (item) => item?.value === patientInfo?.gender,
                        )?.label || 'N/A'
                        
                        let ageText = 'N/A'
                        try {
                          if (patientInfo?.dateOfBirth) {
                            ageText = getAgeFromDobWithTime(patientInfo.dateOfBirth) || 'N/A'
                          }
                        } catch (error) {
                          console.error('Error calculating age:', error)
                        }
                        
                        return (
                          <>
                            {name}
                            <span style={{ color: 'rgba(0, 0, 0, 0.5)', marginLeft: '8px' }}>
                              ({genderLabel}/{ageText}Y)
                            </span>
                          </>
                        )
                      } catch (error) {
                        console.error('Error rendering patient info:', error)
                        return bookedSlot?.userName || bookedSlot?.patientInfo?.name || 'N/A'
                      }
                    })()}
                  </InfoValue>
                </InfoRow>

                {(() => {
                  try {
                    if (!bookedSlot?.patientMeetingSeqNumber) return null
                    const suffix = getOrdinalSuffixForSuperScript(bookedSlot.patientMeetingSeqNumber) || ''
                    return (
                      <InfoRow>
                        <InfoLabel>Visit Number</InfoLabel>
                        <InfoValue>
                          {bookedSlot.patientMeetingSeqNumber}
                          <sup>{suffix}</sup> Visit
                        </InfoValue>
                      </InfoRow>
                    )
                  } catch (error) {
                    console.error('Error rendering visit number:', error)
                    return null
                  }
                })()}

                <InfoRow>
                  <InfoLabel>Event Type</InfoLabel>
                  <InfoValue>
                    {(() => {
                      try {
                        const eventType = displayNamesOfEventTypes?.[bookedSlot?.eventType] || 'Session'
                        const appointmentNumber = bookedSlot?.appointmentNumber
                        return (
                          <>
                            {eventType}
                            {appointmentNumber && (
                              <span style={{ color: 'rgba(0, 0, 0, 0.5)', marginLeft: '8px' }}>
                                Appt No. {appointmentNumber}
                              </span>
                            )}
                          </>
                        )
                      } catch (error) {
                        console.error('Error rendering event type:', error)
                        return 'Session'
                      }
                    })()}
                  </InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>Platform</InfoLabel>
                  <InfoValue>
                    {(() => {
                      try {
                        const isOnline = bookedSlot?.meetingSrc === SESSION_PLATFORM_ONLINE
                        const platform = isOnline ? 'Online' : 'Offline'
                        const roomName = bookedSlot?.chamberRoomName
                        return (
                          <>
                            {platform}
                            {roomName && (
                              <span style={{ color: 'rgba(0, 0, 0, 0.5)', marginLeft: '8px' }}>
                                ({roomName})
                              </span>
                            )}
                          </>
                        )
                      } catch (error) {
                        console.error('Error rendering platform:', error)
                        return 'N/A'
                      }
                    })()}
                  </InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>Time</InfoLabel>
                  <InfoValue>
                    {(() => {
                      try {
                        if (!bookedSlot?.startTime) return 'N/A'
                        const startMoment = dayjs.utc(bookedSlot.startTime)
                        const endMoment = dayjs.utc(bookedSlot.endTime || bookedSlot.startTime)
                        
                        if (!startMoment.isValid() || !endMoment.isValid()) return 'Invalid Time'
                        
                        const timeRange = `${startMoment.local().format('h:mm A')} - ${endMoment.local().format('h:mm A')}`
                        const date = startMoment.local().format('DD MMM YYYY')
                        
                        return (
                          <>
                            {timeRange}
                            <span style={{ color: 'rgba(0, 0, 0, 0.5)', marginLeft: '8px' }}>
                              {date}
                            </span>
                          </>
                        )
                      } catch (error) {
                        console.error('Error rendering time:', error)
                        return 'N/A'
                      }
                    })()}
                  </InfoValue>
                </InfoRow>

                {(() => {
                  try {
                    if (bookedSlot?.meetingSrc !== SESSION_PLATFORM_OFFLINE) return null
                    const currency = (bookedSlot?.currency || '').toUpperCase()
                    const feePaid = bookedSlot?.feePaid || 'N/A'
                    const paymentMethod = OFFLINE_PAYMENT_METHODS_LIST?.find(
                      (item) => item?.id === bookedSlot?.feePaymentMode,
                    )?.title || 'N/A'
                    
                    return (
                      <InfoRow>
                        <InfoLabel>Payment</InfoLabel>
                        <InfoValue>
                          Fees Paid: {currency} {feePaid} ({paymentMethod})
                        </InfoValue>
                      </InfoRow>
                    )
                  } catch (error) {
                    console.error('Error rendering payment info:', error)
                    return null
                  }
                })()}

                {(() => {
                  try {
                    if (!Array.isArray(bookedSlot?.previousSteps)) return null
                    const screeningStep = bookedSlot.previousSteps.find(
                      (item) => item?.refType === 'screening' && item?.status === 'completed',
                    )
                    if (!screeningStep?.stepHandlerName) return null
                    
                    return (
                      <InfoRow>
                        <InfoLabel>Screened By</InfoLabel>
                        <InfoValue>{screeningStep.stepHandlerName}</InfoValue>
                      </InfoRow>
                    )
                  } catch (error) {
                    console.error('Error rendering screening info:', error)
                    return null
                  }
                })()}

                {clinicData && (forMentor || isSupportPortal) && (
                  <InfoRow>
                    <InfoLabel>Clinic</InfoLabel>
                    <InfoValue>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {clinicData.clinicBrandName && (
                          <Chip
                            icon={<Business sx={{ fontSize: 14 }} />}
                            label={clinicData.clinicBrandName}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(67, 56, 202, 0.1)',
                              color: 'rgba(67, 56, 202, 0.9)',
                              fontWeight: 600,
                              fontSize: '11px',
                            }}
                          />
                        )}
                        {clinicData.clinicLocationName && (
                          <Chip
                            icon={<LocationOn sx={{ fontSize: 14 }} />}
                            label={clinicData.clinicLocationName}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(99, 102, 241, 0.1)',
                              color: 'rgba(99, 102, 241, 0.9)',
                              fontWeight: 600,
                              fontSize: '11px',
                            }}
                          />
                        )}
                      </Box>
                    </InfoValue>
                  </InfoRow>
                )}

                <InfoRow>
                  <InfoLabel>Status</InfoLabel>
                  <InfoValue>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <StatusChip
                        label={bookedSlot?.status || 'Unknown'}
                        sx={{
                          backgroundColor: slotColor?.background || 'rgba(0, 0, 0, 0.1)',
                          color: slotColor?.fontColor || 'rgba(0, 0, 0, 0.7)',
                        }}
                      />
                      {bookedSlot?.sessionStatus && (
                        <StatusChip
                          label={BOOKING_SESSION_STATUS_MAP[bookedSlot.sessionStatus]?.label || bookedSlot.sessionStatus}
                          sx={{
                            backgroundColor: 'rgba(67, 56, 202, 0.1)',
                            color: 'rgba(67, 56, 202, 0.9)',
                          }}
                        />
                      )}
                    </Box>
                  </InfoValue>
                </InfoRow>
              </InfoSection>
            </AccordionDetails>
          </StyledAccordion>

          {/* All Actions - Using BookedSlotActionsAccordion to match menu visibility */}
          <BookedSlotActionsAccordion
            bookedSlot={bookedSlot}
            forMentor={forMentor}
            setClickedBookedSlotData={setClickedBookedSlotData}
            setEditPatientDialogOpen={onOpenPatientDetails ? () => onOpenPatientDetails() : undefined}
            setAddVitalsDialogOpen={onAddVitals ? () => onAddVitals() : undefined}
            setEditBookingDialogOpen={onEditBooking ? () => onEditBooking() : undefined}
            handleNewTherapyRequest={handleNewTherapyRequest || (onBookTherapy ? () => onBookTherapy() : undefined)}
            therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
            refreshBookedEvents={refreshBookedEvents}
            markBookedSessionAsCompletedFunction={onMarkCompleted ? async () => {
              if (onMarkCompleted) {
                onMarkCompleted()
              }
            } : undefined}
            handleOpenJoiningDetails={onOpenJoiningDetails}
            openUploadPrescriptionImagesDialog={onOpenUploadPrescription}
            openBookedEventLogs={onOpenEventLogs}
            openEmrImagesDialog={openEmrImagesDialog}
            openSendAssessmentDialog={onSendForAssessment}
            openPatientBookingDialog={onOpenTreatmentHistory}
            openSendTherapyPopover={onSendForTherapy}
            handleToggleRecording={onToggleRecording}
            showPrescriptionPdfDownloadButtons={showPrescriptionPdfDownloadButtons}
            openPrescriptionPdfDownloadDialog={onOpenPrescriptionDownload}
            forceShowJoiningDetailsActionButton={forceShowJoiningDetails}
            openRescheduleDialog={onReschedule}
            hideRescheduleDialog={hideRescheduleDialog}
            showViewReportsButtonInActionButtons={showViewReportsButton}
            openReportViewDrawer={onOpenReportView ? (slot) => onOpenReportView() : undefined}
            openPaymentLinksDialog={onOpenPaymentLinks}
            isMainAccordionExpanded={true}
            openReceiptDialog={openReceiptDialog}
            setEventDataForReceiptPrinting={setEventDataForReceiptPrinting}
          />
        </DrawerContent>
      </DrawerContainer>
    </Drawer>
  )
}

